/**
 * PoseLifter — Core module that lifts a 2D pose stream into 3D world
 * coordinates using a neural lifter (MotionBERT / VideoPose3D).
 *
 * Phase 12 of the Spec→Code migration plan. Runs as a *post-recording*
 * pass: input is the full CanonicalFrame[] sequence, output is a
 * parallel Lifted3DSeries keyed by frameId. Real-time inference is not
 * supported — the windowing strategy assumes the entire sequence is
 * available.
 *
 * docs/architecture-spec.md §6 (ROM Core)
 * docs/migration-plan.md Phase 12
 */

import type { CanonicalFrame } from '../canonical/CanonicalFrame';
import type { CoreModuleDescriptor } from './CoreModule';

const PRODUCER_VERSION = '0.1.0';

/** Per-landmark 3D world position in metres, hip-centred. */
export interface Lifted3DLandmark {
  /** Native landmark index from the source body (0..16 for COCO-17). */
  nativeIndex: number;
  x: number;
  y: number;
  z: number;
  score: number;
}

export interface Lifted3DBody {
  frameLocalId: number;
  persistentTrackingId: number | null;
  landmarks: Lifted3DLandmark[];
}

export interface Lifted3DFrame {
  frameId: number;
  bodies: Lifted3DBody[];
}

export interface Lifted3DSeries {
  streamId: string;
  /** Adapter id, e.g. 'motionbert@1.0', 'videopose3d@1.1'. */
  lifterId: string;
  /** Parallel to the input CanonicalFrame[]. Bodies match by trackId. */
  frames: Lifted3DFrame[];
  producerVersion: string;
}

/** Subset interface a lifter implementation MUST satisfy. */
export interface PoseLifterAdapter {
  readonly id: string;
  readonly windowFrames: number;
  /** Whether this adapter is appropriate for the current device tier. */
  readonly tier: 'pc' | 'mobile';
  initialize(): Promise<void>;
  /** Lift a windowed batch of (numFrames, numJoints, 2) keypoints
   *  into (numFrames, numJoints, 3) world coordinates. The lifter
   *  decides whether to emit per-frame or mid-frame-only. */
  liftWindow(
    keypoints2D: Float32Array,
    numFrames: number,
    numJoints: number,
  ): Promise<Float32Array | null>;
  dispose(): Promise<void>;
}

export interface PoseLifterOptions {
  /** Adapter implementation. Choose MotionBERT for PC, VideoPose3D for
   *  mobile. The host-side device tier picker provides the right one. */
  adapter: PoseLifterAdapter;
  /** Stride between successive windows. Default = windowFrames / 2 for
   *  smooth blending; set to windowFrames for non-overlapping windows. */
  windowStride?: number;
}

export const POSE_LIFTER_DESCRIPTOR: CoreModuleDescriptor = {
  id: 'pose-lifter',
  producerVersion: PRODUCER_VERSION,
  schemaVersionRange: '^1.0.0',
  requires: {
    capabilities: ['pose'],
    minimumJointKeys: [
      'leftShoulder',
      'rightShoulder',
      'leftHip',
      'rightHip',
      'leftKnee',
      'rightKnee',
      'leftAnkle',
      'rightAnkle',
    ],
    confidenceModels: null,
    subjectProfile: false,
  },
  outputType: 'Lifted3DSeries',
};

const NUM_JOINTS_COCO17 = 17;

/**
 * Run the lifter over a full CanonicalFrame sequence with sliding
 * windows. The windows blend in the overlap region by averaging.
 *
 * Returns null when the sequence is shorter than one full window
 * (no clean way to extrapolate without padding artifacts).
 */
export async function liftPoseSequence(
  frames: readonly CanonicalFrame[],
  opts: PoseLifterOptions,
): Promise<Lifted3DSeries | null> {
  const numFrames = frames.length;
  if (numFrames === 0) {
    return {
      streamId: '',
      lifterId: opts.adapter.id,
      frames: [],
      producerVersion: PRODUCER_VERSION,
    };
  }
  const win = opts.adapter.windowFrames;
  if (numFrames < win) return null;
  const stride = Math.max(1, opts.windowStride ?? Math.floor(win / 2));

  // Single-body assumption for the scaffold tier; multi-person lifting
  // requires per-track windowing and is left for a follow-up.
  const trackId = pickPrimaryTrack(frames);
  if (trackId === null) return null;

  await opts.adapter.initialize();

  // Allocate per-frame accumulators for blending.
  const sumXYZ = new Float32Array(numFrames * NUM_JOINTS_COCO17 * 3);
  const counts = new Uint16Array(numFrames * NUM_JOINTS_COCO17);

  // Reusable input buffer for each window.
  const winInput = new Float32Array(win * NUM_JOINTS_COCO17 * 2);

  for (let start = 0; start + win <= numFrames; start += stride) {
    fillWindow2D(frames, start, win, trackId, winInput);
    const lifted = await opts.adapter.liftWindow(winInput, win, NUM_JOINTS_COCO17);
    if (!lifted) continue;
    accumulate(sumXYZ, counts, lifted, start, win);
    if (start + win === numFrames) break;
  }

  await opts.adapter.dispose();

  const outFrames: Lifted3DFrame[] = new Array(numFrames);
  for (let f = 0; f < numFrames; f += 1) {
    const lms: Lifted3DLandmark[] = [];
    for (let j = 0; j < NUM_JOINTS_COCO17; j += 1) {
      const cIdx = f * NUM_JOINTS_COCO17 + j;
      const c = counts[cIdx];
      if (c === 0) continue;
      const o = cIdx * 3;
      lms.push({
        nativeIndex: j,
        x: sumXYZ[o] / c,
        y: sumXYZ[o + 1] / c,
        z: sumXYZ[o + 2] / c,
        score: 1,
      });
    }
    outFrames[f] = {
      frameId: frames[f].frameId,
      bodies: [{ frameLocalId: trackId, persistentTrackingId: null, landmarks: lms }],
    };
  }

  return {
    streamId: frames[0].streamId,
    lifterId: opts.adapter.id,
    frames: outFrames,
    producerVersion: PRODUCER_VERSION,
  };
}

function pickPrimaryTrack(frames: readonly CanonicalFrame[]): number | null {
  for (const f of frames) {
    if (f.pose.bodies.length > 0) return f.pose.bodies[0].frameLocalId;
  }
  return null;
}

function fillWindow2D(
  frames: readonly CanonicalFrame[],
  start: number,
  win: number,
  trackId: number,
  out: Float32Array,
): void {
  // Layout: [frame, joint, xy] flat. Out length = win * 17 * 2.
  out.fill(0);
  for (let i = 0; i < win; i += 1) {
    const frame = frames[start + i];
    const body = frame.pose.bodies.find((b) => b.frameLocalId === trackId);
    if (!body) continue;
    for (const lm of body.landmarks) {
      const idx = lm.raw.nativeIndex;
      if (idx < 0 || idx >= NUM_JOINTS_COCO17) continue;
      const w = lm.pos2D.sourceWidthPx || 1;
      const h = lm.pos2D.sourceHeightPx || 1;
      const off = (i * NUM_JOINTS_COCO17 + idx) * 2;
      // Lifters expect coordinates in image-normalised [0,1] space.
      out[off] = lm.pos2D.xPx / w;
      out[off + 1] = lm.pos2D.yPx / h;
    }
  }
}

function accumulate(
  sumXYZ: Float32Array,
  counts: Uint16Array,
  lifted: Float32Array,
  start: number,
  win: number,
): void {
  // lifted layout: [frame, joint, xyz] flat.
  for (let i = 0; i < win; i += 1) {
    for (let j = 0; j < NUM_JOINTS_COCO17; j += 1) {
      const liftIdx = (i * NUM_JOINTS_COCO17 + j) * 3;
      const accIdx = ((start + i) * NUM_JOINTS_COCO17 + j) * 3;
      sumXYZ[accIdx] += lifted[liftIdx];
      sumXYZ[accIdx + 1] += lifted[liftIdx + 1];
      sumXYZ[accIdx + 2] += lifted[liftIdx + 2];
      counts[(start + i) * NUM_JOINTS_COCO17 + j] += 1;
    }
  }
}

export const POSE_LIFTER_PRODUCER_VERSION = PRODUCER_VERSION;
